import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, ArrowRightIcon, LockIcon } from 'lucide-react';
import { SubpageHeader } from '../components/SubpageHeader';
import { LockedListing } from '../components/category/LockedListing';
import { InviteWall } from '../components/InviteWall';
import { useCollectionWall } from '../hooks/useCollectionWall';
import { categories } from '../data/categories';
type CategoryPageProps = {
  slug: string;
};
const ease = [0.22, 1, 0.36, 1] as const;
export function CategoryPage({
  slug
}: CategoryPageProps) {
  const category = categories.find(c => c.slug === slug) ?? categories[0];
  const reduceMotion = useReducedMotion();
  const collectionRef = useRef<HTMLElement>(null);
  const {
    walled,
    dismiss: dismissWall
  } = useCollectionWall(collectionRef, category.slug);
  const others = categories.filter(c => c.slug !== category.slug);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${category.title} — Superluxury`;
  }, [category]);
  const rise = (delay = 0) => ({
    initial: {
      opacity: 0,
      y: reduceMotion ? 0 : 18
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 1.1,
      ease,
      delay
    }
  });
  const reveal = {
    initial: {
      opacity: 0,
      y: reduceMotion ? 0 : 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.9,
      ease
    }
  };
  return <div className="bg-paper text-ink" data-id="element-1426" data-mp="3q8xl1nhjxh6i">
      <SubpageHeader tone="dark" overlay data-id="element-1427" data-mp="3q9yxnwe7ubqc" />

      <main data-id="element-1428" data-mp="3la439iv8f40j">
        {/* Opening */}
        <section aria-labelledby="cat-title" className="relative flex min-h-[520px] items-end overflow-hidden bg-ink text-paper md:min-h-[64vh]" data-id="element-1429" data-mp="3l92r1b2hcbh8">
          <motion.img key={category.heroImage} src={category.heroImage} alt={category.heroAlt} className="absolute inset-0 h-full w-full object-cover" initial={{
          opacity: 0,
          scale: reduceMotion ? 1 : 1.06
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 2.2,
          ease
        }} data-id="element-1430" data-mp="29v7d25nba8rl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0.35)_50%,rgba(0,0,0,0.35))]" aria-hidden="true" data-id="element-1431" data-mp="2bbl4avzpnbuj" />
          <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.07] motion-reduce:hidden" aria-hidden="true" data-id="element-1432" data-mp="22b6fhmnjfema" />

          <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-6 pb-12 md:px-16 md:pb-16" data-id="element-1433" data-mp="1nl8m8sar3yq8">
            <motion.p {...rise(0.2)} className="text-[15px] font-light text-paper/80" data-id="element-1434" data-mp="1setdm1hu9ugg">
              What we arrange
            </motion.p>
            <motion.h1 {...rise(0.35)} id="cat-title" className="mt-3 font-editorial text-[64px] leading-[64px] tracking-[-0.02em] md:text-[120px] md:leading-[112px]" data-id="element-1435" data-mp="1infkcbghdqvr">
              {category.title}
            </motion.h1>
            <motion.p {...rise(0.5)} className="mt-5 max-w-[560px] text-[19px] font-light leading-[28px] text-paper/90 md:text-[22px] md:leading-[32px]" data-id="element-1436" data-mp="19nadq3j42win">
              {category.tagline}
            </motion.p>
            <motion.div {...rise(0.65)} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-1437" data-mp="l377wuwnhaxo">
              <Link to="/membership" className="inline-flex h-[54px] items-center gap-3 bg-paper px-7 text-[16px] font-medium text-ink transition-opacity duration-150 hover:opacity-85" data-id="element-1438" data-mp="1006f4hnlb1rm">
                Apply for membership
              </Link>
              <a href="#collection" className="group inline-flex items-center gap-2 text-[16px] font-light text-paper/85 transition-colors duration-150 hover:text-paper" data-id="element-1439" data-mp="18xqibw50j84v">
                View the private collection
                <ArrowDownIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-1440" data-mp="1dr36k55s5cx3" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Compact overview: what we offer, the two ways, how we curate */}
        <section aria-label={`About ${category.title}`} className="border-b border-line" data-id="element-1441" data-mp="3h7zsdh2fx24t">
          <motion.div {...reveal} className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-6 py-12 md:px-16 md:py-14 lg:grid-cols-12 lg:gap-14" data-id="element-1442" data-mp="3m6jcrd1dy5v2">
            <div className="lg:col-span-5" data-id="element-1443" data-mp="3qy1ei3b8gah3">
              <p className="font-editorial text-[26px] leading-[32px] tracking-[-0.01em] md:text-[30px] md:leading-[36px]" data-id="element-1444" data-mp="3vwmcrx6xck2w">{category.statement}</p>
              <ol className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] font-light text-ink/65" aria-label={category.curationHeading} data-id="element-1445" data-mp="2ya5i6zpkskku">
                {category.curation.map((step, i) => <li key={step.title} className="inline-flex items-center gap-3" data-id="element-1446" data-mp="385gqaz6hatjm" data-mpi={i}>
                    {i > 0 && <ArrowRightIcon className="h-3.5 w-3.5 text-ink/40" strokeWidth={1.25} aria-hidden="true" data-id="element-1447" data-mp="3ca6k8ihkty1w" />}
                    {step.title}
                  </li>)}
              </ol>
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:col-span-7 lg:gap-10" data-id="element-1448" data-mp="rkh7cb0fombu">
              {category.ways.map((way, i) => <div key={way.title} className={i > 0 ? 'border-t border-line pt-8 sm:border-l sm:border-t-0 sm:pl-10 sm:pt-0' : ''} data-id="element-1449" data-mp="27ybmk7vvlf0y" data-mpi={i}>
                  <p className="text-[13px] font-light text-ink/60" data-id="element-1450" data-mp="236k30hb509kb">{way.label}</p>
                  <h2 className="mt-1.5 text-[22px] font-normal leading-[28px]" data-id="element-1451" data-mp="1y7z4qnfg3zyi">{way.title}</h2>
                  <p className="mt-2 text-[15px] font-light leading-[24px] text-ink/75" data-id="element-1452" data-mp="1tb08nzc81ixo">{way.text}</p>
                </div>)}
            </div>
          </motion.div>
        </section>

        {/* Locked collection */}
        <section ref={collectionRef} id="collection" aria-labelledby="collection-heading" className="scroll-mt-10" data-id="element-1453" data-mp="1k9ivaoq77vco">
          <div className="mx-auto max-w-[1440px] px-6 py-14 md:px-16 md:py-16" data-id="element-1454" data-mp="1fcipmex9gum3">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-1455" data-mp="3emih4zl69lse">
              <div data-id="element-1456" data-mp="39oyt57yo9un9">
                <p className="inline-flex items-center gap-2 text-[14px] font-light text-ink/60" data-id="element-1457" data-mp="39mcn5crfjn75">
                  <LockIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-1458" data-mp="3sqzbfdlr3e39" />
                  For members only
                </p>
                <h2 id="collection-heading" className="mt-3 font-editorial text-[34px] leading-[38px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-1459" data-mp="3jsx5qkr2nx9n">
                  {category.collectionHeading}
                </h2>
              </div>
              <p className="max-w-[420px] text-[16px] font-light leading-[26px] text-ink/70" data-id="element-1460" data-mp="g9at7t5ktn7w">{category.collectionNote}</p>
            </div>

            <ul className="mt-10 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3" data-id="element-1461" data-mp="6dqzndehj0mb">
              {category.listings.map(listing => <li key={listing.title} data-id="element-1462" data-mp="bahi27dri8aw">
                  <LockedListing listing={listing} data-id="element-1463" data-mp="2r2x7h4r2tifr" />
                </li>)}
            </ul>

            <div className="mt-14 flex items-center justify-between border-y border-line py-6" aria-hidden="true" data-id="element-1464" data-mp="2w32u26ihoeqz">
              <span className="select-none text-[18px] font-light text-ink/70 blur-[5px]" data-id="element-1465" data-mp="2ohz8bncs1w9g">{category.heldCount}</span>
              <LockIcon className="h-4 w-4 text-ink/60" strokeWidth={1.5} data-id="element-1466" data-mp="2jjfj9e2w04cv" />
            </div>
            <p className="sr-only" data-id="element-1467" data-mp="2xifb0ulp4zd9">{category.heldCount}, shown to members on introduction.</p>

            {/* Two ways in */}
            <div className="mt-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between" data-id="element-1468" data-mp="2sij2ci220jrd">
              <p className="max-w-[520px] text-[16px] font-light leading-[26px] text-ink/75" data-id="element-1469" data-mp="24soru757j7aa">{category.accessLine}</p>
              <div className="flex flex-wrap gap-3" data-id="element-1470" data-mp="2ekukzk773ur2">
                <Link to="/membership" className="inline-flex h-[52px] items-center bg-ink px-7 text-[16px] font-medium text-paper transition-opacity duration-150 hover:opacity-85" data-id="element-1471" data-mp="2emfbmyqe2tjn">
                  Apply for membership
                </Link>
                <Link to="/private-line" className="inline-flex h-[52px] items-center border border-ink px-7 text-[16px] font-medium transition-colors duration-150 hover:bg-ink hover:text-paper" data-id="element-1472" data-mp="1h4xfj3jo2u90">
                  Request an introduction
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Other categories */}
        <nav aria-label="Other categories" className="border-t border-line" data-id="element-1473" data-mp="1akpls7naqosp">
          <ul className="mx-auto grid max-w-[1440px] grid-cols-1 px-6 md:grid-cols-3 md:px-16" data-id="element-1474" data-mp="1fhpruik5brjt">
            {others.map((c, i) => <li key={c.slug} className={i > 0 ? 'border-t border-line md:border-l md:border-t-0' : ''} data-id="element-1475" data-mp="1jhq9sd6gekvm" data-mpi={i}>
                <Link to={`/${c.slug}`} className="group flex items-center justify-between py-8 md:px-8 md:py-10" data-id="element-1476" data-mp="1t9wacbaruamf">
                  <span className="font-editorial text-[30px] leading-none" data-id="element-1477" data-mp="1y98takyiuxjq">{c.title}</span>
                  <ArrowRightIcon className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.25} aria-hidden="true" data-id="element-1478" data-mp="237txevgwa1dg" />
                </Link>
              </li>)}
          </ul>
        </nav>
      </main>

      {walled && <InviteWall title={category.wallTitle} body={category.wallBody} onClose={dismissWall} data-id="element-1479" data-mp="3ipq5sy9em3df" />}
    </div>;
}